/**
 * Equipment Register Hook (FT-251 rewrite)
 *
 * Equipment storage holds ONLY user-added + archived items.
 * Required / derived items are computed at render time from `vesselRequirements`
 * (via `transformSelectionsToRegister`).
 *
 * All reads/writes flow through `useStorageState`'s single subscription.
 * `syncFromRequirements` is preserved as a backward-compatible no-op stub
 * with the rich return shape `EquipmentRecordsTab.js` expects (§9 of brief).
 *
 * See /reports/ACTIVE/FT-251-equipment-hook-rewrite/CODING-BRIEF-v1.1.md
 */

import { useEffect, useMemo, useCallback, useRef } from 'react';
import { useStorageState } from '../useStorageState';
import { devLog } from '../../utils/devLog';
import { useVessel } from '../../contexts/VesselContext';
import {
  loadUserEquipmentSelections,
  transformSelectionsToRegister,
} from '../../services/equipment/selectionTransformer';
import { usePermissions } from '../usePermissions';
import { createAuditEntry, appendToAuditLog } from '../../utils/auditUtils';
import { isTombstoned } from '../../sync/tombstone';  // FT-499: filter hard-deleted items from all display views

// ─── Helpers (preserved unchanged from prior hook) ──────────────────────────

// Merge user fields from one item to another
const mergeUserFields = (base, user) => ({
  ...base,
  serialNo: user.serialNo ?? user.serialNumber ?? base.serialNo,
  installedAt: user.installedAt ?? base.installedAt,
  notes: user.notes ?? base.notes,
  photos: (user.photos && user.photos.length) ? user.photos : base.photos,
  customFields: { ...(base.customFields || {}), ...(user.customFields || {}) },
  status: user.status ?? base.status,
  updatedAt: user.updatedAt ?? base.updatedAt,
});

// Infer location from equipment label when location field is missing
const inferLocationFromLabel = (label) => {
  if (!label) return null;
  const lower = label.toLowerCase();

  if (lower.includes('galley') || lower.includes('kitchen')) return 'galley';
  if (lower.includes('engine') || lower.includes('machinery')) return 'engine';
  if (lower.includes('accommodation') || lower.includes('cabin')) return 'accommodation';
  if (lower.includes('bridge') || lower.includes('wheelhouse')) return 'bridge';
  if (lower.includes('deck')) return 'deck';

  return null;
};

// Normalize equipment name aliases to canonical forms
const normalizeNameAliases = (name, type) => {
  if (!name) return '';
  const lower = name.toLowerCase().trim();

  if (lower.includes('extinguisher') || type === 'extinguisher') {
    if (lower.includes('5a/34b')) return 'Fire Extinguisher (5A/34B)';
    if (lower.includes('8a/68b') && lower.includes('additional')) return 'Fire Extinguisher (8A/68B) - Additional';
    if (lower.includes('8a/68b')) return 'Fire Extinguisher (8A/68B)';
    return 'Fire Extinguisher';
  }

  if (lower.includes('blanket') || type === 'blanket') {
    return 'Fire Blanket';
  }

  if (lower.includes('fixed') && (lower.includes('fire') || lower.includes('system')) || type === 'fixedSystem') {
    return 'Fixed Fire System';
  }

  if (lower.includes('detector') && lower.includes('fire')) {
    return 'Fire Detector';
  }

  if (lower.includes('detector') && (lower.includes('gas') || lower.includes('co') || lower.includes('carbon'))) {
    if (lower.includes('co') || lower.includes('carbon')) return 'Carbon Monoxide Detector';
    return 'Hydrocarbon Gas Detector';
  }

  return name.trim();
};

// Canonical key for equipment deduplication.
// Requirement-sourced items dedupe by sourceRequirementId; user items by composite key.
const canonicalKey = (row) => {
  if (row.source === 'requirements' && row.sourceRequirementId) {
    return `req:${row.sourceRequirementId}`;
  }

  const type = (row.type || '').toLowerCase().trim();
  const locRaw = (row.location || inferLocationFromLabel(row.label || row.name));
  const location = (locRaw || 'general').toLowerCase().trim();
  const name = normalizeNameAliases(row.label || row.name, type);
  const variant = (row.variant || 'default').toLowerCase().trim();
  return `user:${type}|${name}|${location}|${variant}`;
};

// De-duplicate items using canonical keys, preferring requirements but preserving user fields
const dedupeAndPreferRequirements = (arr) => {
  const map = new Map();
  for (const row of arr) {
    const k = canonicalKey(row);
    if (!map.has(k)) {
      map.set(k, row);
      continue;
    }

    const existing = map.get(k);
    const isReq = (x) => x.source === 'requirements';

    if (isReq(row) && !isReq(existing)) {
      map.set(k, mergeUserFields(row, existing));
    } else if (!isReq(row) && isReq(existing)) {
      map.set(k, existing);
    } else {
      const score = (x) =>
        (x.serialNo || x.serialNumber ? 1 : 0) +
        (x.notes ? 1 : 0) +
        ((x.photos && x.photos.length) ? 1 : 0) +
        (x.lastServiceAt ? 1 : 0);
      map.set(k, score(row) > score(existing) ? row : existing);
    }
  }
  return Array.from(map.values());
};

// ─── Migration (§7) ─────────────────────────────────────────────────────────

const USER_EDIT_FIELDS = [
  'serialNo', 'serialNumber', 'modelType', 'specifications',
  'notes', 'installedAt', 'customFields', 'photos', 'status',
  'auditLog', // v1.1: items with audit history are user-touched even if no field edit
];

const hasUserEdits = (item) =>
  USER_EDIT_FIELDS.some(f => {
    const v = item[f];
    if (v == null) return false;
    if (typeof v === 'string') return v.trim() !== '';
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'object') return Object.keys(v).length > 0;
    return true;
  });

function migrateInitialIDBLoad(items) {
  if (!Array.isArray(items)) return [];
  // Ultra-conservative: keep ALL stored items. Normalise source so every item
  // is routed through the user-added selectors (the new selectors in §5E filter
  // on source === 'added' / 'requirements'). Render-time dedup
  // (dedupeAndPreferRequirements) collapses any duplicates with auto-derived
  // items. Zero risk of accidental data loss for items where source/markers
  // are missing or non-canonical.
  return items.map(item => {
    if (item.source === 'added' || item.archived === true) return item;
    return { ...item, source: 'added' };
  });
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useEquipmentRegister() {
  const { getCurrentVessel, currentVesselId } = useVessel();
  const currentVessel = getCurrentVessel();
  const { currentUser } = usePermissions();

  // ──────────── 5A. Single storage subscription ────────────
  const { value: storedItems, setValue: setStoredItems, loaded } =
    useStorageState('equipment_register', []);

  // ──────────── 5B. One-shot migration of initial IDB load ────────────
  // Filter out derived items from prior code (source === 'requirements' AND no user edits).
  // Keep: source === 'added', archived === true, OR items with any user-facing field populated.
  const migratedOnceRef = useRef(false);
  useEffect(() => {
    if (!loaded) return;
    if (migratedOnceRef.current) return;
    migratedOnceRef.current = true;

    const current = storedItems || [];
    const filtered = migrateInitialIDBLoad(current);
    if (filtered.length !== current.length) {
      devLog('[useEquipmentRegister] migrateInitialIDBLoad dropped',
        current.length - filtered.length, 'derived-only items');
      setStoredItems(filtered);
    }
    // intentionally only `loaded` in deps — see §7 of brief.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  // ──────────── 5C. Derived (required) items, computed at render time ────────────
  const derivedItems = useMemo(() => {
    if (!loaded || !currentVesselId || !currentVessel) return [];
    const userSelections = loadUserEquipmentSelections(currentVesselId);
    if (!userSelections?.selections) return [];
    const mapped = transformSelectionsToRegister(
      userSelections.selections,
      currentVesselId,
      currentVessel
    ) || [];
    // CRITICAL (v1.1): mapper returns items with `source: 'user_selection'`.
    // Selectors in §5E filter on `source === 'requirements'`. Normalise here
    // so the Required tab is non-empty. Without this map, requiredItems = [].
    return mapped.map(i => ({ ...i, source: 'requirements' }));
  }, [loaded, currentVesselId, currentVessel]);

  // ──────────── 5D. Combined view (render-time merge) ────────────
  const combinedItems = useMemo(() => {
    // FT-499: exclude hard-deleted tombstones from ALL display paths.
    // Tombstoned items (deleted_at stamp) are kept in raw storage for sync
    // propagation but must never surface in any UI view — not even the archived tab.
    const live = (storedItems || []).filter(i => !isTombstoned(i));
    const userAdded = live.filter(i => i.source === 'added' && !i.archived);
    const archived  = live.filter(i => i.archived);
    const merged = dedupeAndPreferRequirements([...derivedItems, ...userAdded]);
    return [...merged, ...archived];
  }, [storedItems, derivedItems]);

  // ──────────── 5E. Selectors ────────────
  const requiredItems = useMemo(
    () => combinedItems.filter(i => i.source === 'requirements' && !i.archived),
    [combinedItems]
  );
  const addedItems = useMemo(
    () => combinedItems.filter(i => i.source === 'added' && !i.archived),
    [combinedItems]
  );
  const archivedItems = useMemo(
    () => combinedItems.filter(i => i.archived),
    [combinedItems]
  );
  const reviewItems = useMemo(
    () => combinedItems.filter(i => i.evaluation === 'uncertain' && !i.archived),
    [combinedItems]
  );
  const items = useMemo(
    () => combinedItems.filter(i => !i.archived),
    [combinedItems]
  );

  const counts = useMemo(() => ({
    required: requiredItems.length,
    added:    addedItems.length,
    archived: archivedItems.length,
    review:   reviewItems.length,
    total:    items.length,
  }), [requiredItems, addedItems, archivedItems, reviewItems, items]);

  // ──────────── 5F. CRUD operations — ALL via setStoredItems ────────────
  const addItem = useCallback((item) => {
    if (!loaded) return null; // defense-in-depth
    const newItem = {
      ...item,
      id: item.id || `user_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      source: 'added',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setStoredItems(prev => [...(prev || []), newItem]);
    return newItem;
  }, [loaded, setStoredItems]);

  const updateItem = useCallback((id, updates) => {
    if (!loaded) return false;
    setStoredItems(prev => {
      const target = (prev || []).find(i => i.id === id);
      if (!target) {
        // EDIT-OF-DERIVED case: derived item being edited for the first time.
        // Adopt into storage as source: 'added' so the user edits survive.
        const derived = derivedItems.find(d => d.id === id);
        if (!derived) return prev;
        const adopted = {
          ...derived,
          ...updates,
          source: 'added', // CRITICAL: promote so it persists
          updatedAt: new Date().toISOString(),
          updatedBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role },
          auditLog: appendToAuditLog([], createAuditEntry(derived, { ...derived, ...updates }, currentUser)),
        };
        return [...(prev || []), adopted];
      }
      // Normal case: item exists in storage already
      return prev.map(i => {
        if (i.id !== id) return i;
        const auditEntry = createAuditEntry(i, { ...i, ...updates }, currentUser);
        return {
          ...i,
          ...updates,
          updatedAt: new Date().toISOString(),
          updatedBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role },
          auditLog: appendToAuditLog(i.auditLog, auditEntry),
        };
      });
    });
    return true;
  }, [loaded, setStoredItems, derivedItems, currentUser]);

  const archiveItem = useCallback((id, reason = 'User archived') => {
    if (!loaded) return false;
    setStoredItems(prev => {
      const now = new Date().toISOString();
      const target = (prev || []).find(i => i.id === id);
      if (!target) {
        // ARCHIVE-OF-DERIVED case: adopt then archive.
        const derived = derivedItems.find(d => d.id === id);
        if (!derived) return prev;
        return [...(prev || []), {
          ...derived,
          archived: true,
          archivedAt: now,
          archivedReason: reason,
          // FT-499: stamp updatedAt so this local write wins LWW over a stale
          // non-archived cloud copy. Without this, the cloud's older updatedAt
          // still beats us in mergePullDelta and the archive flag is lost.
          updatedAt: now,
        }];
      }
      return prev.map(i => i.id === id
        ? { ...i, archived: true, archivedAt: now, archivedReason: reason, updatedAt: now }
        : i
      );
    });
    return true;
  }, [loaded, setStoredItems, derivedItems]);

  const removeItem = useCallback(async (id) => {
    if (!loaded) return false;
    // FT-499: tombstone instead of hard-filter. The sync bridge (UniversalSyncBridge
    // mergePullDelta + retainTombstone) uses the FT-458 snake-case `deleted_at` marker
    // to make deletions sticky: a stale non-tombstoned cloud copy can never resurrect
    // the item. The tombstone is filtered from ALL display views via isTombstoned()
    // in combinedItems (§5D), so the user sees the item gone immediately.
    // NEVER hard-remove from the array — the cloud∪local union in mergePullDelta
    // reseeds any absent item from the cloud copy and pushes it back.
    const now = new Date().toISOString();
    setStoredItems(prev => {
      const list = prev || [];
      const target = list.find(i => i.id === id);
      if (!target) {
        // Item may be derived (not in storage). Adopt it first so the tombstone
        // exists in the persisted array and propagates to cloud.
        const derived = derivedItems.find(d => d.id === id);
        if (!derived) return list; // already gone — no-op
        return [...list, { ...derived, source: 'added', deleted_at: now, updatedAt: now }];
      }
      return list.map(i => i.id === id
        ? { ...i, deleted_at: now, updatedAt: now }
        : i
      );
    });
    return true;
  }, [loaded, setStoredItems, derivedItems]);

  // FT-254: Restore an archived item by clearing the archived flag.
  // Symmetric to archiveItem. Idempotent — if item isn't archived (or doesn't exist),
  // no-op success. Preserves the archivedAt / archivedReason fields as audit trail
  // (rather than deleting them) so a restored item retains its history.
  const restoreItem = useCallback((id) => {
    if (!loaded) return false;
    setStoredItems(prev => (prev || []).map(i => i.id === id
      ? { ...i, archived: false, restoredAt: new Date().toISOString() }
      : i
    ));
    return true;
  }, [loaded, setStoredItems]);

  // ──────────── 5G. Getters (function form, for legacy consumers) ────────────
  const getRequired     = useCallback(() => requiredItems, [requiredItems]);
  const getUserAdded    = useCallback(() => addedItems,    [addedItems]);
  const getArchived     = useCallback(() => archivedItems, [archivedItems]);
  const getReviewNeeded = useCallback(() => reviewItems,   [reviewItems]);
  const getAllItems     = useCallback(() => items,         [items]);
  const getFilteredItems = useCallback((filter) => {
    switch (filter) {
      case 'required': return requiredItems;
      case 'added':    return addedItems;
      case 'review':   return reviewItems;
      case 'archived': return archivedItems;
      case 'all':
      default:         return items;
    }
  }, [requiredItems, addedItems, archivedItems, reviewItems, items]);

  // ──────────── 5H. No-op sync stub (backward compat — §9) ────────────
  const syncFromRequirements = useCallback(async (_dryRun = false) => {
    devLog('[useEquipmentRegister] syncFromRequirements is a no-op stub (FT-251)');
    return {
      success: true,
      applicableCount: derivedItems.length,
      created:   [],
      updated:   [],
      archived:  [],
      unchanged: [],
      changeCount: 0,
    };
  }, [derivedItems.length]);

  const syncRequiredOnce = useCallback(async (dryRun) =>
    syncFromRequirements(dryRun), [syncFromRequirements]);

  // ──────────── 5I. Return surface ────────────
  return {
    // Arrays + counts
    requiredItems,
    addedItems,
    archivedItems,
    reviewItems,
    counts,

    // Sync stubs
    syncFromRequirements,
    syncRequiredOnce,

    // Legacy
    items,
    userItems: storedItems, // raw storage contents

    // Aliases (§8 — required by callsites)
    register: items,
    equipment: items,

    // Getters
    getRequired,
    getUserAdded,
    getArchived,
    getReviewNeeded,
    getAllItems,
    getFilteredItems,

    // Legacy count names
    requiredCount: counts.required,
    addedCount:    counts.added,
    archivedCount: counts.archived,
    totalCount:    counts.total,

    // CRUD
    addItem,
    updateItem,
    archiveItem,
    restoreItem, // FT-254
    removeItem,

    // State
    syncing: false,
    error:   null,
    lastSyncAt: null,

    // Feature flag
    isFeatureEnabled: () => true,

    // Debug
    debugState: {
      currentVesselId,
      loaded,
      storedItemsCount: (storedItems || []).length,
      derivedItemsCount: derivedItems.length,
      combinedItemsCount: combinedItems.length,
    },
    dumpAllItems: () => combinedItems,

    // Dev-only stub (§8): kept so `EquipmentRecordsTab.js` destructure remains valid.
    // Removed in FT-251; returns benign empty audit shape.
    runGlobalGuidanceAudit: () => ({
      current: { currentTotal: 0, currentWithGuidance: 0, currentWithoutGuidance: 0, missingItems: [] },
      global:  { globalTotal: 0,  globalWithGuidance: 0,  globalWithoutGuidance: 0,  missingByDomain: {} },
    }),
  };
}

